import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_URL } from '../../config';
import AdminLayout from './AdminLayout';
import './Admin.css';

function UserManagement() {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterRole, setFilterRole] = useState('all');
    const [filterPlan, setFilterPlan] = useState('all');
    const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0 });
    const [selectedUser, setSelectedUser] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);
    const { currentUser } = useAuth();

    useEffect(() => {
        fetchUsers();
    }, [pagination.page, filterRole, filterPlan]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);

            if (!currentUser) {
                throw new Error('Not authenticated');
            }

            const token = await currentUser.getIdToken();
            const params = new URLSearchParams({
                page: pagination.page,
                limit: pagination.limit,
                ...(searchQuery && { search: searchQuery }),
                ...(filterRole !== 'all' && { role: filterRole }),
                ...(filterPlan !== 'all' && { plan: filterPlan })
            });

            const response = await fetch(`${API_URL}/api/admin/users?${params}`, {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({}));
                throw new Error(errorData.error || `Server error: ${response.status}`);
            }

            const data = await response.json();
            if (data.success) {
                setUsers(data.users || []);
                setPagination(prev => ({ ...prev, total: data.total || 0 }));
            } else {
                setUsers([]);
            }
        } catch (err) {
            console.error('Users fetch error:', err);
            setError(err.message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        setPagination(prev => ({ ...prev, page: 1 }));
        fetchUsers();
    };

    const handleUserAction = async (userId, action) => {
        if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

        try {
            setActionLoading(true);
            const token = await currentUser.getIdToken();

            const response = await fetch(`${API_URL}/api/admin/users/${userId}/status`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ action })
            });

            if (!response.ok) throw new Error('Action failed');

            const data = await response.json();
            if (data.success) {
                fetchUsers();
                setSelectedUser(null);
            }
        } catch (err) {
            alert('Failed to perform action: ' + err.message);
        } finally {
            setActionLoading(false);
        }
    };

    const formatDate = (timestamp) => {
        if (!timestamp) return 'N/A';
        const date = timestamp._seconds
            ? new Date(timestamp._seconds * 1000)
            : new Date(timestamp);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getStatusBadge = (user) => {
        if (user.banned) return <span className="badge badge-danger">Banned</span>;
        if (user.suspended) return <span className="badge badge-warning">Suspended</span>;
        return <span className="badge badge-success">Active</span>;
    };

    const getPlanBadge = (plan) => {
        if (plan === 'premium') return <span className="badge badge-premium">Premium</span>;
        return <span className="badge badge-free">Free</span>;
    };

    return (
        <AdminLayout>
            <div className="admin-users">
                <div className="admin-header">
                    <h1>User Management</h1>
                    <span className="total-count">{pagination.total} total users</span>
                </div>

                {/* Filters */}
                <div className="filters-bar">
                    <form onSubmit={handleSearch} className="search-form">
                        <input
                            type="text"
                            placeholder="Search by email or name..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="search-input"
                        />
                        <button type="submit" className="search-btn">Search</button>
                    </form>

                    <div className="filter-selects">
                        <select
                            value={filterRole}
                            onChange={(e) => setFilterRole(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Roles</option>
                            <option value="user">Users</option>
                            <option value="admin">Admins</option>
                            <option value="super_admin">Super Admins</option>
                        </select>

                        <select
                            value={filterPlan}
                            onChange={(e) => setFilterPlan(e.target.value)}
                            className="filter-select"
                        >
                            <option value="all">All Plans</option>
                            <option value="free">Free</option>
                            <option value="premium">Premium</option>
                        </select>
                    </div>
                </div>

                {/* Users Table */}
                {loading ? (
                    <div className="admin-loading-container">
                        <div className="spinner"></div>
                        <p>Loading users...</p>
                    </div>
                ) : error ? (
                    <div className="admin-error">
                        <p>{error}</p>
                        <button onClick={fetchUsers}>Retry</button>
                    </div>
                ) : (
                    <>
                        <div className="table-container">
                            <table className="admin-table">
                                <thead>
                                    <tr>
                                        <th>User</th>
                                        <th>Plan</th>
                                        <th>Role</th>
                                        <th>Status</th>
                                        <th>Joined</th>
                                        <th>Last Active</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {users.map(user => (
                                        <tr key={user.id}>
                                            <td className="user-cell">
                                                <div className="user-avatar">
                                                    {user.displayName?.[0] || user.email?.[0] || '?'}
                                                </div>
                                                <div className="user-info">
                                                    <span className="user-name">{user.displayName || 'No Name'}</span>
                                                    <span className="user-email">{user.email}</span>
                                                </div>
                                            </td>
                                            <td>{getPlanBadge(user.subscription?.plan || 'free')}</td>
                                            <td>
                                                <span className={`role-badge role-${user.role || 'user'}`}>
                                                    {user.role || 'user'}
                                                </span>
                                            </td>
                                            <td>{getStatusBadge(user)}</td>
                                            <td>{formatDate(user.createdAt)}</td>
                                            <td>{formatDate(user.lastLogin)}</td>
                                            <td>
                                                <button
                                                    className="action-btn view"
                                                    onClick={() => setSelectedUser(user)}
                                                >
                                                    View
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Pagination */}
                        <div className="pagination">
                            <button
                                disabled={pagination.page <= 1}
                                onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}
                            >
                                Previous
                            </button>
                            <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}</span>
                            <button
                                disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}
                            >
                                Next
                            </button>
                        </div>
                    </>
                )}

                {/* User Detail Modal */}
                {selectedUser && (
                    <div className="modal-overlay" onClick={() => setSelectedUser(null)}>
                        <div className="modal-content" onClick={e => e.stopPropagation()}>
                            <div className="modal-header">
                                <h2>User Details</h2>
                                <button className="close-btn" onClick={() => setSelectedUser(null)}>×</button>
                            </div>

                            <div className="modal-body">
                                <div className="user-detail-section">
                                    <h3>Profile</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Email</label>
                                            <span>{selectedUser.email}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Display Name</label>
                                            <span>{selectedUser.displayName || 'Not set'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>User ID</label>
                                            <span className="user-id">{selectedUser.id}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Role</label>
                                            <span>{selectedUser.role || 'user'}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="user-detail-section">
                                    <h3>Subscription</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Plan</label>
                                            <span>{selectedUser.subscription?.plan || 'free'}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Topics Used</label>
                                            <span>{selectedUser.subscription?.topicsUsed || 0}</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="user-detail-section">
                                    <h3>Activity</h3>
                                    <div className="detail-grid">
                                        <div className="detail-item">
                                            <label>Joined</label>
                                            <span>{formatDate(selectedUser.createdAt)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Last Login</label>
                                            <span>{formatDate(selectedUser.lastLogin)}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Total XP</label>
                                            <span>{selectedUser.totalXP || 0}</span>
                                        </div>
                                        <div className="detail-item">
                                            <label>Current Streak</label>
                                            <span>{selectedUser.currentStreak || 0} days</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-actions">
                                {!selectedUser.banned ? (
                                    <button
                                        className="action-btn danger"
                                        onClick={() => handleUserAction(selectedUser.id, 'ban')}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? 'Processing...' : 'Ban User'}
                                    </button>
                                ) : (
                                    <button
                                        className="action-btn success"
                                        onClick={() => handleUserAction(selectedUser.id, 'unban')}
                                        disabled={actionLoading}
                                    >
                                        {actionLoading ? 'Processing...' : 'Unban User'}
                                    </button>
                                )}
                                <button
                                    className="action-btn warning"
                                    onClick={() => handleUserAction(selectedUser.id, 'reset_password')}
                                    disabled={actionLoading}
                                >
                                    Send Password Reset
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </AdminLayout>
    );
}

export default UserManagement;
